import { planClock, type Plan, type Schedule } from "@69ly/shared";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PLAY_SPEEDS, type Playhead } from "../../lib/plan.ts";
import { Icon } from "../Icon.tsx";

/** The chapter the playhead is in: from its start to the next one's (or the end). */
export function chapterRange(plan: Plan, schedule: Schedule, length: number, elapsed: number): { from: number; to: number; name: string } | null {
  const starts = plan.chapters.map((c) => ({ name: c.name, at: schedule.toElapsed(c.at) })).sort((a, b) => a.at - b.at);
  for (let i = starts.length - 1; i >= 0; i--)
    if (elapsed >= starts[i].at - 0.01) return { name: starts[i].name, from: starts[i].at, to: starts[i + 1]?.at ?? length };
  return null;
}

/**
 * Play, pause, step from one event to the next, the clock, the speed, chapters
 * and looping one. Space plays and pauses, the arrows step, while the page has the keys.
 */
export function PlanControls({ plan, schedule, length, events, playhead }: { plan: Plan; schedule: Schedule; length: number; events: number[]; playhead: Playhead }) {
  const { t } = useTranslation();
  const { elapsed, playing, toggle, seek, speed, setSpeed, loop, setLoop } = playhead;
  const prev = () => seek([...events].reverse().find((e) => e < elapsed - 0.05) ?? 0);
  const next = () => seek(events.find((e) => e > elapsed + 0.05) ?? length);
  const chapter = chapterRange(plan, schedule, length, elapsed);

  // Keys for the player, unless typing somewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest("input, textarea, select, [contenteditable], [role=slider]")) return;
      if (e.key === " " && !el?.closest("button")) {
        e.preventDefault();
        toggle();
      } else if (e.key === "ArrowRight" && e.altKey) next();
      else if (e.key === "ArrowLeft" && e.altKey) prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="plan-controls" role="group" aria-label={t("plan.controls")}>
      <button type="button" className="btn btn-quiet btn-icon" onClick={prev} aria-label={t("plan.prevEvent")} title={`${t("plan.prevEvent")} (Alt+←)`}>
        <Icon name="prev" size={14} />
      </button>
      <button type="button" className="btn btn-primary btn-small plan-play" onClick={toggle} aria-label={playing ? t("plan.pause") : t("plan.play")} title={`${playing ? t("plan.pause") : t("plan.play")} (Space)`}>
        <Icon name={playing ? "pause" : "play"} size={14} />
      </button>
      <button type="button" className="btn btn-quiet btn-icon" onClick={next} aria-label={t("plan.nextEvent")} title={`${t("plan.nextEvent")} (Alt+→)`}>
        <Icon name="next" size={14} />
      </button>
      <span className="plan-clock mono" aria-live="off">
        {planClock(schedule.toClock(elapsed))}
      </span>
      {chapter && <span className="plan-chapter-now small">{chapter.name}</span>}
      <div className="segmented segmented-small" role="group" aria-label={t("plan.speed")}>
        {PLAY_SPEEDS.map((s) => (
          <button key={s} type="button" className="seg" aria-pressed={speed === s} onClick={() => setSpeed(s)}>
            {s}×
          </button>
        ))}
      </div>
      {plan.chapters.length > 0 && (
        <select
          className="plan-chapter-pick"
          aria-label={t("plan.chapters")}
          value=""
          onChange={(e) => {
            const c = plan.chapters.find((x) => x.id === e.target.value);
            if (c) seek(schedule.toElapsed(c.at));
          }}
        >
          <option value="">{t("plan.jumpTo")}</option>
          {[...plan.chapters]
            .sort((a, b) => b.at - a.at)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {planClock(c.at)} · {c.name}
              </option>
            ))}
        </select>
      )}
      <button
        type="button"
        className="btn btn-quiet btn-small"
        aria-pressed={loop !== null}
        title={chapter ? t("plan.loopChapter", { name: chapter.name }) : t("plan.loopAll")}
        onClick={() => setLoop(loop ? null : chapter ? { from: chapter.from, to: chapter.to } : { from: 0, to: length })}
      >
        <Icon name="loop" size={14} />
        {loop ? t("plan.looping") : t("plan.loop")}
      </button>
    </div>
  );
}
